// Boot — TODO T17 (30 Aug 2026); the theme added at T30 (1 Sep 2026), tap to talk's engines at
// T57 (10 Sep 2026).
//
// Provider order is not arbitrary: ApiProvider needs Clerk's `getToken`, and the query hooks
// need both, so Clerk wraps Query wraps Api wraps Theme wraps the router. The voice sits inside
// the router, around the app, because only the kadmon panel reads it.
import { ClerkProvider } from '@clerk/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useMemo, useRef, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApiProvider, useRequest } from './api/provider.tsx';
import { createQueryClient } from './api/queryClient.ts';
import { speechApi } from './api/speech.ts';
import type { FinishedAsk } from './silence.ts';
import { voiceApi } from './api/voice.ts';
import { browserRealtimeLink, browserWarmUp } from './realtime.ts';
import { browserElevenLabsLink, browserElevenLabsWarmUp } from './elevenlabs.ts';
import { browserFillerAudio } from './voice-fillers.ts';
import { App } from './app/App.tsx';
import { preloadScreens } from './app/screens.ts';
import { StartupFailed } from './app/Gate.tsx';
import { applyAccent, applyTheme, readAccent, readTheme, ThemeProvider } from './app/theme.tsx';
import { browserVoice, transcribeVoice, VoiceProvider, type Voice } from './app/voice.tsx';
import { readEnv, type ConsoleEnv } from './env.ts';
import './styles/tokens.css';
import './styles/app.css';

// Before the first paint, not in an effect: a recruiter who chose dark would otherwise see one
// light frame at every reload, which reads as a flicker rather than as a setting.
applyTheme(readTheme());
applyAccent(readAccent());

const container = document.getElementById('root');
if (!container) throw new Error('index.html has no #root');
const root = createRoot(container);

let env;
try {
  env = readEnv(import.meta.env);
} catch (error) {
  // A missing key is a setup problem, and the person hitting it is the person who can fix it.
  // Rendering the message beats a blank page and a brain trace nobody opens.
  root.render(<StartupFailed error={error as Error} />);
  throw error;
}

// Tap to talk's engines, decided once (src/env.ts): Amazon Transcribe where VITE_VOICE_INPUT says
// `transcribe` — the deployed brain's, since T57b — the browser's own where it says `browser`,
// none otherwise. Transcribe's asks the API for the URL each dictation opens, so it is made inside
// ApiProvider, once, and takes the request function as it stands at each tap.
const voiceInput: ConsoleEnv['voiceInput'] = env.voiceInput;
// Her voice (T66): Polly through the API where VITE_VOICE_OUTPUT says `polly` — the deployed
// brain's — else the device's own.
const voiceOutput: ConsoleEnv['voiceOutput'] = env.voiceOutput;

function VoiceEngines({ children }: { children: ReactNode }) {
  const request = useRequest();
  const latest = useRef(request);
  latest.current = request;
  const voice = useMemo<Voice | null>(() => {
    const say =
      voiceOutput === 'polly' ? (text: string) => speechApi(latest.current).say(text) : null;
    // T451c: a model's read of whether the words heard so far are finished, so the mic ends on
    // the thought and not only on a fixed silence (src/silence.ts).
    const finished: FinishedAsk = (text, mode) => speechApi(latest.current).finished(text, mode);
    // T67: the conversation on OpenAI's voice model, where the browser can open a call; the
    // server's `/me` says whether this instance has the key.
    const open = browserRealtimeLink(window);
    const warm = browserWarmUp(window) ?? undefined;
    // `POST /voice/session` answers for whichever vendor the server is set up for; each
    // conversation takes only its own (23 Sep 2026).
    const realtime = open
      ? {
          session: async () => {
            const session = await voiceApi(latest.current).session();
            if (session.vendor !== 'openai') throw new Error('this server talks on ElevenLabs');
            return session;
          },
          open,
          warm,
        }
      : null;
    // 23 Sep 2026: the conversation on ElevenLabs' agent, where the server has one. It needs no
    // input or voice of this build's, so it is offered whatever VITE_VOICE_INPUT says.
    const openElevenLabs = browserElevenLabsLink(window);
    const elevenlabs = openElevenLabs
      ? {
          session: async () => {
            const session = await voiceApi(latest.current).session();
            if (session.vendor !== 'elevenlabs') throw new Error('this server talks on OpenAI');
            return session;
          },
          open: openElevenLabs,
          warm: browserElevenLabsWarmUp(window),
          // T120: what she says while a lookup runs, in her voice (voice-fillers.ts).
          fillers: browserFillerAudio(window) ?? undefined,
        }
      : null;
    if (voiceInput === 'transcribe') {
      const voice = transcribeVoice(
        window,
        () => speechApi(latest.current).dictation(),
        say,
        realtime,
        finished,
      );
      return { ...voice, elevenlabs };
    }
    if (voiceInput === 'browser') return { ...browserVoice(window, say, finished), elevenlabs };
    return elevenlabs ? { input: null, output: null, elevenlabs } : null;
  }, []);
  return <VoiceProvider voice={voice}>{children}</VoiceProvider>;
}

root.render(
  <StrictMode>
    <ClerkProvider publishableKey={env.clerkPublishableKey} afterSignOutUrl="/">
      <QueryClientProvider client={createQueryClient()}>
        <ApiProvider baseUrl={env.bffUrl} release={env.release}>
          <ThemeProvider>
            <BrowserRouter>
              <VoiceEngines>
                <App />
              </VoiceEngines>
            </BrowserRouter>
          </ThemeProvider>
        </ApiProvider>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
);

// Every screen's file, now, while Clerk signs in: each is a file of its own since 24 Sep 2026 and
// is drawn at once when it has arrived (app/lazyScreen.tsx).
preloadScreens();
